/**
 * FDV — «Añadir selección» de la landing de campaña (`sections/fdv-promo-bundles.liquid`).
 *
 * Añade TODAS las variantes/cantidades de una selección en una sola llamada y lo hace con el
 * mecanismo del tema, igual que `product-form.js#processBatchAddToCart`:
 *   1. despacha `CartLinesUpdateEvent` (acción `add`) con una promesa diferida → el cart drawer
 *      se abre solo, el ícono del carrito y el motor de regalos (`fdv-gift-engine`) reaccionan;
 *   2. POST /cart/add.js con `items[]` + `sections` (Section Rendering del carrito);
 *   3. resuelve la promesa con las secciones nuevas → el drawer se re-pinta con `morphSection`.
 *
 * ⚠️ /cart/add.js con varios ítems NO es atómico: si falta stock añade lo que puede y responde
 * 422 (verificado contra la tienda: pedir 1 + 999 añadió las existencias disponibles). Por eso:
 *   · ANTES de añadir se compara cada línea contra sus existencias (`data-max`) sumando lo que el
 *     cliente ya tiene en el carrito; si no alcanza, no se envía nada y se explica qué producto falla;
 *   · si aun así Shopify responde 422 (carrera de stock), se muestra su motivo y se re-pinta el
 *     carrito con el estado REAL — sin afirmar que "no se añadió nada".
 *
 * No calcula ni promete precios: el descuento lo aplica y muestra el carrito.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const REENABLE_MS = 600;

// Una sola cola para las selecciones de esta página. En un carrito nuevo, varios
// POST /cart/add.js simultáneos pueden crear carritos distintos antes de que el
// navegador guarde la primera cookie: todas las tarjetas dicen "añadido", pero
// solo queda la selección del último carrito. Reservar el turno antes de leer
// el carrito permite además comprobar existencias contra lo añadido previamente.
let bundleAddQueue = Promise.resolve();
function reserveBundleAddTurn() {
  const previous = bundleAddQueue;
  let release;
  bundleAddQueue = new Promise((resolve) => { release = resolve; });
  return { previous, release };
}

const routes = () => (window.Theme && window.Theme.routes) || {};

function promoContext() {
  const root = document.querySelector('fdv-promo-state');
  let previewMode = null;
  if (root?.dataset.forced === 'true') previewMode = 'theme_editor';
  else if (root?.dataset.allowPreview !== 'false' && new URLSearchParams(location.search).has('promo_at')) previewMode = 'url';
  return {
    campaign_id: root?.dataset.campaignId || null,
    campaign_name: root?.dataset.campaignName || null,
    promo_state: document.documentElement.dataset.fdvPromoState || null,
    preview_mode: previewMode,
  };
}

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && typeof cartItems.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }
  const res = await fetch(`${routes().cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
  return res.json();
}

/** HTML actual del carrito vía Section Rendering (para re-pintar el drawer tras un 422 parcial). */
async function fetchSections(ids) {
  if (!ids.length) return undefined;
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${encodeURIComponent(ids.join(','))}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.ok ? await res.json() : undefined;
  } catch {
    return undefined;
  }
}

class FdvPromoBundle extends HTMLElement {
  #busy = false;
  #reenable = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#reenable);
  }

  #items() {
    return [...this.querySelectorAll('li[data-variant-id]')]
      .map((li) => ({
        id: Number(li.dataset.variantId),
        quantity: Number(li.dataset.quantity) || 1,
        max: li.dataset.max === undefined ? null : Number(li.dataset.max),
        title: li.dataset.title || '',
      }))
      .filter((it) => Number.isFinite(it.id) && it.id > 0);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add]') : null;
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    this.#add(button);
  };

  #say(text, { error = false } = {}) {
    const status = this.querySelector('[data-status]');
    const err = this.querySelector('[data-error]');
    if (status) status.textContent = error ? '' : text;
    if (err) {
      err.textContent = error ? text : '';
      err.hidden = !error;
    }
  }

  async #add(button) {
    if (this.#busy) return;
    const items = this.#items();
    if (!items.length) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    this.#say('');
    const turn = reserveBundleAddTurn();
    let dispatched = false;

    try {
      await turn.previous;
      // 1) Existencias vs. lo que ya hay en el carrito (antes de tocar nada).
      const before = await fetchCart();
      const inCart = new Map();
      for (const line of before.items || []) inCart.set(line.variant_id, (inCart.get(line.variant_id) || 0) + line.quantity);
      const short = items.filter((it) => it.max != null && (inCart.get(it.id) || 0) + it.quantity > it.max);
      if (short.length) {
        const detail = short
          .map((it) => {
            const have = inCart.get(it.id) || 0;
            const left = Math.max(0, it.max - have);
            return `${it.title || 'Un producto'}: quedan ${left} disponibles${have ? ` (ya tienes ${have} en tu carrito)` : ''}`;
          })
          .join('; ');
        this.#say(`No pudimos añadir la selección: ya no quedan existencias suficientes. ${detail}.`, { error: true });
        return;
      }

      // 2) Añadir.
      const totalQty = items.reduce((n, it) => n + it.quantity, 0);
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const deferred = CartLinesUpdateEvent.createPromise();
      dispatched = true;
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
          promise: deferred.promise,
        })
      );

      const res = await fetch(routes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items.map(({ id, quantity }) => ({ id, quantity })), sections: sectionIds.join(',') }),
      });
      const body = await res.json();

      if (body.status || !res.ok) {
        // 422: Shopify pudo haber añadido parte. Se re-pinta el carrito real y se avisa sin prometer nada.
        const reason = body.description || body.message || 'Uno de los productos no está disponible.';
        this.dispatchEvent(
          new CartErrorEvent({
            error: body.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: body.description, errors: body.errors },
          })
        );
        const [cart, sections] = await Promise.all([fetchCart().catch(() => null), fetchSections(sectionIds)]);
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'fdv-promo-bundle', sourceId: this.id, itemCount: totalQty, sections },
          });
        } else {
          deferred.reject(new Error(reason));
        }
        this.#say(`${reason} Revisa tu carrito: la selección puede haberse añadido solo en parte.`, { error: true });
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'fdv-promo-bundle',
          sourceId: this.id,
          itemCount: totalQty,
          sections: body.sections,
          didError: false,
        },
      });
      this.#say(this.dataset.addedText || 'Selección añadida al carrito.');
      window.FDV?.track?.('fdv_promo_bundle_added', {
        ...promoContext(),
        placement: this.dataset.placement || null,
        bundle_id: this.dataset.bundleId,
        bundle_title: this.dataset.bundleTitle,
        line_count: items.length,
        item_count: totalQty,
      });
    } catch (error) {
      console.error('[fdv-promo-bundle]', error);
      if (dispatched) {
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
        );
      }
      this.#say('No pudimos añadir la selección. Revisa tu conexión e inténtalo de nuevo.', { error: true });
    } finally {
      turn.release();
      this.#reenable = setTimeout(() => {
        this.#busy = false;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }, REENABLE_MS);
    }
  }
}

if (!customElements.get('fdv-promo-bundle')) customElements.define('fdv-promo-bundle', FdvPromoBundle);
